/**
 * PostgREST filter-string safety (pure, browser/server/extension safe).
 *
 * supabase-js `.or("a.ilike.%x%,b.ilike.%x%")` takes ONE string in PostgREST's
 * own filter grammar, where `,` separates conditions and `(` `)` nest groups.
 * Interpolating raw user text into it lets the text rewrite the predicate:
 *
 *     q = "a,email.ilike.%"  ->  an extra, attacker-written condition
 *     q = "x),(name.eq.y"    ->  the group closes early and a new one opens
 *
 * `tsc` cannot see it (the argument is just a string). Free-text search terms go
 * through `buildIlikeOrFilter` / `sanitizePostgrestSearchTerm`; never interpolate
 * user text into `.or(` by hand.
 *
 * Two strategies, one per kind of value:
 *
 * - FREE TEXT (a search box): STRIP. Structural characters become a space, so no
 *   value can need quoting, and a LIKE metacharacter the user typed is escaped so
 *   it matches itself. `buildIlikeOrFilter` / `filterSearch`.
 * - EXACT VALUES (an email, an id, a cursor, a list of ids): QUOTE. The value is
 *   wrapped in `"` with `\` and `"` backslash-escaped, which is PostgREST's own
 *   quoting, so a value may contain `,` `(` `)` `.` `:` and still be ONE value.
 *   `filterCondition` / `filterIn` / `filterArray` / `filterLike`.
 *
 * Builders return a `FilterFragment`; `orFilter` / `andGroup` / `orGroup` combine
 * fragments, so a call site never hand-joins a string with commas. A fragment
 * can only be made by a builder, which is what the repo's
 * `validate:postgrest-filter-interpolation` guard relies on.
 */
/** Longest search term we keep. Autocomplete and search boxes never need more. */
export const POSTGREST_SEARCH_MAX_LENGTH = 64;
// Control chars + PostgREST/LIKE structure: , ( ) " ' \ * (`*` is PostgREST's
// alias for `%` inside like/ilike values).
// eslint-disable-next-line no-control-regex
const STRIP_RE = /[\u0000-\u001f\u007f,()"'\\*]/g;
const LIKE_WILDCARD_RE = /[%_]/g;
const COLUMN_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;
/**
 * Reduce free text to something safe to embed in a PostgREST `ilike` value.
 *
 * 1. structural characters -> space, whitespace collapsed + trimmed
 * 2. truncated to `maxLength`
 * 3. `%` and `_` backslash-escaped so they match literally (an email such as
 *    `john_smith@x.com` still finds john_smith, not johnXsmith)
 *
 * Returns "" when nothing searchable remains.
 */
export function sanitizePostgrestSearchTerm(raw, options = {}) {
    const maxLength = options.maxLength ?? POSTGREST_SEARCH_MAX_LENGTH;
    const cleaned = raw.replace(STRIP_RE, " ").replace(/\s+/g, " ").trim().slice(0, maxLength).trim();
    return cleaned.replace(LIKE_WILDCARD_RE, (c) => `\\${c}`);
}
/**
 * Build the string for `.or(...)`: `col1.ilike.%term%,col2.ilike.%term%`.
 *
 * Returns null when the sanitized term is shorter than `minLength` — the caller
 * must then skip the filter (or return an empty result), never fall back to the
 * raw input. `columns` are developer-supplied identifiers and are validated;
 * a bad one throws, because that is a programming error, not user input.
 */
export function buildIlikeOrFilter(columns, raw, options = {}) {
    if (columns.length === 0)
        throw new Error("buildIlikeOrFilter: at least one column is required");
    for (const column of columns) {
        if (!COLUMN_RE.test(column)) {
            throw new Error(`buildIlikeOrFilter: invalid column name "${column}"`);
        }
    }
    const term = sanitizePostgrestSearchTerm(raw, options);
    // Escapes add characters; judge length on what the user can see.
    const visibleLength = term.replace(/\\([%_])/g, "$1").length;
    if (visibleLength < (options.minLength ?? 1))
        return null;
    return columns.map((column) => `${column}.ilike.%${term}%`).join(",");
}
// ---------------------------------------------------------------------------
// Fragments: exact-value builders + combinators
// ---------------------------------------------------------------------------
/**
 * One validated PostgREST condition (`col.op.value`) or group (`and(...)`).
 * Obtain one from a builder below; pass them to `orFilter` / `andGroup`.
 */
export class FilterFragment {
    constructor(text) {
        this.text = text;
    }
}
// Plain column, optionally with a JSON path: `metadata->>emailId`.
const FILTER_COLUMN_RE = /^[A-Za-z_][A-Za-z0-9_]*(->>?[A-Za-z_][A-Za-z0-9_]*)*$/;
// eslint-disable-next-line no-control-regex
const CONTROL_RE = /[\u0000-\u001f\u007f]/g;
function assertColumn(fn, column) {
    if (!FILTER_COLUMN_RE.test(column)) {
        throw new Error(`${fn}: invalid column name "${column}"`);
    }
}
/**
 * Wrap `value` as ONE PostgREST value: `"..."` with `\` and `"` escaped. Inside
 * the quotes `,` `(` `)` `.` `:` `{` `}` are all ordinary characters. Control
 * characters are dropped (they cannot occur in a legitimate id or email).
 */
export function quotePostgrestValue(value) {
    return `"${value.replace(CONTROL_RE, "").replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
}
function scalarText(fn, value) {
    if (typeof value === "string")
        return quotePostgrestValue(value);
    if (typeof value === "number") {
        if (!Number.isFinite(value))
            throw new Error(`${fn}: value must be a finite number`);
        return String(value);
    }
    return value ? "true" : "false";
}
/** `col.eq."value"` — the value is quoted, so it can be an email, id or timestamp. */
export function filterCondition(column, operator, value) {
    assertColumn("filterCondition", column);
    return new FilterFragment(`${column}.${operator}.${scalarText("filterCondition", value)}`);
}
/** `col.is.null` / `col.is.true` / `col.is.false`. */
export function filterIs(column, value) {
    assertColumn("filterIs", column);
    return new FilterFragment(`${column}.is.${value}`);
}
/**
 * `col.in.("a","b")`. Returns null for an empty list: an empty IN matches
 * nothing, so inside an OR just let `orFilter` drop it — but never read the null
 * as "no filter" where the result is AND-ed.
 */
export function filterIn(column, values) {
    assertColumn("filterIn", column);
    if (values.length === 0)
        return null;
    return new FilterFragment(`${column}.in.(${values.map((v) => scalarText("filterIn", v)).join(",")})`);
}
/**
 * Array column contains / is-contained-by / overlaps: `col.cs.{"a","b"}`.
 * The `{...}` is a Postgres array literal whose elements are double-quoted with
 * the same `\` / `"` escapes as `quotePostgrestValue`. `*` is NOT a wildcard
 * here (only in like/ilike), so it is kept. Null for an empty list.
 */
export function filterArray(column, operator, values) {
    assertColumn("filterArray", column);
    if (values.length === 0)
        return null;
    return new FilterFragment(`${column}.${operator}.{${values.map(quotePostgrestValue).join(",")}}`);
}
/**
 * LIKE/ILIKE on an EXACT value (an id prefix, an email domain suffix). Unlike
 * `filterSearch` nothing is stripped: `\`, `%` and `_` are escaped so they match
 * themselves, `*` (PostgREST rewrites it to `%` and cannot be escaped) becomes
 * `_` — one-any-char, which still matches a literal `*` — and the pattern is
 * quoted. Null when the value is empty.
 */
export function filterLike(column, value, options = {}) {
    assertColumn("filterLike", column);
    const clean = value.replace(CONTROL_RE, "");
    if (clean.length === 0)
        return null;
    const escaped = clean.replace(/[\\%_]/g, (c) => `\\${c}`).replace(/\*/g, "_");
    const match = options.match ?? "contains";
    const pattern = match === "contains"
        ? `%${escaped}%`
        : match === "startsWith"
            ? `${escaped}%`
            : match === "endsWith"
                ? `%${escaped}`
                : escaped;
    return new FilterFragment(`${column}.${options.operator ?? "ilike"}.${quotePostgrestValue(pattern)}`);
}
/**
 * LIKE/ILIKE with a caller-written PATTERN (`%` and `_` keep their wildcard
 * meaning). For developer constants such as `%recurring%`; the pattern is only
 * quoted, so it can never add a condition. For anything a user typed use
 * `filterSearch` (free text) or `filterLike` (exact value).
 */
export function filterPattern(column, pattern, operator = "ilike") {
    assertColumn("filterPattern", column);
    return new FilterFragment(`${column}.${operator}.${quotePostgrestValue(pattern)}`);
}
/**
 * ONE free-text ILIKE condition (`col.ilike.%term%`), sanitized by STRIP like
 * `buildIlikeOrFilter`. Null when nothing searchable remains (or the term is
 * shorter than `minLength`) — skip the filter, never fall back to the raw input.
 */
export function filterSearch(column, raw, options = {}) {
    const text = buildIlikeOrFilter([column], raw, options);
    return text === null ? null : new FilterFragment(text);
}
function joinFragments(parts) {
    const texts = [];
    for (const part of parts)
        if (part)
            texts.push(part.text);
    return texts.length === 0 ? null : texts.join(",");
}
/**
 * Combine fragments for `.or(...)`. Falsy entries are dropped; null when none
 * remain (the caller then decides what "no condition" means).
 */
export function orFilter(parts) {
    return joinFragments(parts);
}
/**
 * `orFilter` for a call site whose fragments are all unconditional (every one is
 * always built): an empty result is a programming error, so it throws instead of
 * returning null.
 */
export function requireOrFilter(parts) {
    const filter = joinFragments(parts);
    if (filter === null)
        throw new Error("requireOrFilter: at least one condition is required");
    return filter;
}
/** `and(a,b)` as a fragment, for nesting inside an OR. Null when empty. */
export function andGroup(parts) {
    const inner = joinFragments(parts);
    return inner === null ? null : new FilterFragment(`and(${inner})`);
}
/** `or(a,b)` as a fragment, for nesting inside an AND. Null when empty. */
export function orGroup(parts) {
    const inner = joinFragments(parts);
    return inner === null ? null : new FilterFragment(`or(${inner})`);
}
function isScalar(value) {
    return typeof value === "string" || typeof value === "number" || typeof value === "boolean";
}
function isListValue(value) {
    return (Array.isArray(value) && value.every((v) => typeof v === "string" || typeof v === "number"));
}
/**
 * One row of a "field / operator / value" filter UI -> a fragment, or null when
 * the row is unusable (unknown operator, a field that is not a plain column,
 * a value of the wrong shape, an empty `in` list, an empty like value). The
 * field, operator and value arrive from the client (hence `unknown`), so
 * nothing here throws: an unusable row is simply not applied.
 *
 * `like` / `ilike` match the value as a substring (literal, via `filterLike`);
 * `in` accepts an array or a comma-separated string.
 */
export function filterAdvanced(field, operator, value) {
    if (!FILTER_COLUMN_RE.test(field))
        return null;
    switch (operator) {
        case "eq":
        case "neq":
        case "gt":
        case "gte":
        case "lt":
        case "lte":
            if (!isScalar(value))
                return null;
            if (typeof value === "number" && !Number.isFinite(value))
                return null;
            return filterCondition(field, operator, value);
        case "like":
        case "ilike":
            if (!isScalar(value))
                return null;
            return filterLike(field, String(value), { operator, match: "contains" });
        case "in": {
            if (isListValue(value))
                return filterIn(field, value);
            if (!isScalar(value))
                return null;
            return filterIn(field, String(value)
                .split(",")
                .map((v) => v.trim())
                .filter((v) => v.length > 0));
        }
        default:
            return null;
    }
}
